import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Rete di sicurezza contro la pagina bianca.
 *
 * Un errore durante il rendering, in React, smonta l'intero albero: l'utente si
 * ritrova davanti a uno schermo vuoto, senza sapere se sia colpa della rete, del
 * telefono o dell'app — e senza niente da toccare. È il peggior modo possibile
 * di fallire, soprattutto in viaggio, dove l'app serve proprio nei momenti in
 * cui non hai tempo di indagare.
 *
 * Questo contenitore trasforma quel vuoto in un messaggio con due vie d'uscita:
 * ricaricare, o tornare alla schermata di oggi.
 *
 * Deve essere una classe: React non offre un equivalente con gli hook.
 */

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly errore: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { errore: null };

  static getDerivedStateFromError(errore: Error): State {
    return { errore };
  }

  override componentDidCatch(errore: Error, info: ErrorInfo): void {
    // In produzione non c'e' una console aperta, ma se qualcuno la apre per
    // capire cosa sia successo deve trovarci qualcosa di utile.
    console.error('Errore di rendering:', errore, info.componentStack);
  }

  override render(): ReactNode {
    const { errore } = this.state;
    if (errore === null) return this.props.children;

    return (
      <div
        role="alert"
        className="mx-auto flex min-h-dvh max-w-[var(--content-max-width)] flex-col items-center justify-center gap-4 px-6 text-center"
      >
        <h1 className="font-display text-[22px] leading-7 text-text">
          Qualcosa si è rotto in questa schermata
        </h1>
        <p className="text-[15px] leading-6 text-text-muted">
          Il resto dell’app funziona. I tuoi dati sono al sicuro sul server: non hai perso
          niente di quello che avevi salvato.
        </p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="min-h-[48px] rounded-[var(--r-md)] bg-brand-strong px-5 text-[16px] font-semibold text-on-brand"
          >
            Ricarica
          </button>
          <button
            type="button"
            onClick={() => {
              // Assegnazione diretta e non `navigate`: se il router fa parte del
              // problema, non ci si puo' appoggiare a lui per uscirne.
              window.location.href = '/oggi';
            }}
            className="min-h-[48px] rounded-[var(--r-md)] border border-border px-5 text-[16px] text-text"
          >
            Torna a Oggi
          </button>
        </div>

        {/* Dettaglio tecnico richiudibile: inutile a chi viaggia, prezioso a chi
            deve capire cosa è successo. */}
        <details className="mt-2 w-full text-left">
          <summary className="cursor-pointer text-[13.5px] text-text-subtle">
            Dettaglio tecnico
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-[var(--r-sm)] bg-surface-sunken p-3 text-[12px] leading-4 text-text-muted">
            {errore.message}
          </pre>
        </details>
      </div>
    );
  }
}
